import React, { forwardRef, useRef } from "react";
import { X, Type, Smile, Music, PenTool, MoreHorizontal, ArrowRight, Church, Trash2, CalendarClock, Check, Eye, Sparkles } from "lucide-react";
import { clamp, localeOf } from "./storyEditorUtils";
import { imageVariant } from "@/lib/mediaUrl";
import { audienceOf, audienceAccent } from "@/lib/postAudience";

const roundBtn = "h-10 w-10 grid place-items-center rounded-full bg-black/40 text-white backdrop-blur-md active:scale-95 transition-transform";

// Barre du haut : fermer + outils (Texte, Stickers, Musique, Dessin, Filtres si média) + œil « Aperçu » quand la story a du contenu.
export const StoryTopBar = ({ onClose, onTool, hasMusic, hasMedia = false, hasFx = false, onPreview, t }) => {
  const tools = [
    { key: "text", Icon: Type, label: t("story.tool_text", "Texte"), testId: "story-tool-text-btn" },
    { key: "stickers", Icon: Smile, label: t("story.tool_stickers", "Stickers"), testId: "story-tool-stickers-btn" },
    { key: "music", Icon: Music, label: t("story.tool_music", "Musique"), testId: "story-tool-music-btn", dot: hasMusic },
    { key: "draw", Icon: PenTool, label: t("story.tool_draw", "Dessin"), testId: "story-tool-draw-btn" },
    ...(hasMedia ? [{ key: "fx", Icon: Sparkles, label: t("story.tool_fx", "Filtres"), testId: "story-tool-fx-btn", dot: hasFx }] : []),
  ];
  return (
    <div className="absolute top-0 inset-x-0 z-30 px-3 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent pb-6" style={{ paddingTop: "calc(var(--sa-top, 0px) + 10px)" }} data-testid="story-fs-topbar">
      <button type="button" onClick={onClose} aria-label={t("common.close", "Fermer")} className={roundBtn} data-testid="story-fs-close">
        <X size={20} />
      </button>
      <div className="flex items-center gap-2">
        {tools.map(({ key, Icon, label, testId, dot }) => (
          <button key={key} type="button" onClick={() => onTool(key)} aria-label={label} className={`${roundBtn} relative`} data-testid={testId}>
            {key === "text" ? <span className="text-[15px] font-bold leading-none">Aa</span> : <Icon size={19} />}
            {dot && <span className="absolute top-1 right-1 h-2 w-2 rounded-full" style={{ background: "var(--yalola-primary)" }} />}
          </button>
        ))}
        {onPreview && (
          <button type="button" onClick={onPreview} aria-label={t("story.preview", "Aperçu")} className={roundBtn} data-testid="story-tool-preview-btn">
            <Eye size={19} />
          </button>
        )}
      </div>
    </div>
  );
};

// Barre de publication : « Ma story » ○ · « Église » ○ (si autorisé) · ⋯ (programmer, options) · → (feuille de partage).
// Mode édition (annonce d'église déjà programmée) : puce « Programmée · date » + bouton « Enregistrer ».
export const StoryBottomBar = ({ user, churches, onMyStory, onChurch, onShare, onMore, busy, scheduled, editMode = false, scheduledAt = "", onSchedule, onSave, audience = "public", lang, t }) => {
  const initial = (user?.name || "?").trim()[0]?.toUpperCase();
  const churchLabel = churches.length === 1 ? churches[0].name : t("story.church_story", "Église");
  const schedLabel = scheduledAt ? new Date(scheduledAt).toLocaleString(localeOf(lang), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : t("story.schedule", "Programmer la publication");
  const aud = audienceOf(audience); // lot 651 — repère d'audience sur « Ma story » (icône + libellé si restreinte)
  return (
    <div className="absolute bottom-0 inset-x-0 z-30 px-3 pt-8 flex items-center gap-2 bg-gradient-to-t from-black/85 via-black/40 to-transparent" style={{ paddingBottom: "calc(var(--sa-bottom, 0px) + 12px)" }} data-testid="story-fs-bottombar" data-edit={editMode ? 1 : 0}>
      {editMode ? (
        <button type="button" onClick={onSchedule} className="flex items-center gap-2 pl-3 pr-3.5 py-2 rounded-full bg-white/15 backdrop-blur-xl border border-white/20 active:scale-95 transition-transform min-w-0" data-testid="story-fs-edit-schedule-chip">
          <CalendarClock size={16} style={{ color: "var(--yalola-primary)" }} />
          <span className="text-[12px] font-semibold text-white truncate">{t("story.scheduled_badge", "Programmée")} · {schedLabel}</span>
        </button>
      ) : (
        <>
          <button type="button" onClick={onMyStory} disabled={busy} className="flex items-center gap-2 pl-1 pr-3.5 py-1 rounded-full bg-white/15 backdrop-blur-xl border border-white/20 active:scale-95 transition-transform disabled:opacity-50 min-w-0" data-testid="story-publish-my-story-btn" data-audience={aud.key}>
            <span className="relative shrink-0">
              {user?.avatar_url
                ? <img src={imageVariant(user.avatar_url, 48)} alt="" className="h-8 w-8 rounded-full object-cover border-2" style={{ borderColor: audienceAccent(aud.key) }} />
                : <span className="h-8 w-8 rounded-full grid place-items-center text-white text-sm font-bold border-2" style={{ background: "var(--yalola-primary)", borderColor: audienceAccent(aud.key) }}>{initial}</span>}
              {aud.key !== "public" && <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full grid place-items-center text-white border border-black/60" style={{ background: audienceAccent(aud.key) }} data-testid="story-fs-audience-badge"><aud.icon size={10} /></span>}
            </span>
            <span className="min-w-0 text-left leading-tight">
              <span className="block text-[12px] font-semibold text-white truncate">{t("story.my_story", "Ma story")}</span>
              {aud.key !== "public" && <span className="block text-[10px] text-white/75 truncate">{t(aud.tkey, aud.label)}</span>}
            </span>
          </button>
          {churches.length > 0 && (
            <button type="button" onClick={onChurch} disabled={busy} className="flex items-center gap-2 pl-1 pr-3.5 py-1 rounded-full bg-sky-500/20 backdrop-blur-xl border border-sky-400/40 active:scale-95 transition-transform disabled:opacity-50 min-w-0" data-testid="story-publish-church-story-btn">
              <span className="h-8 w-8 rounded-full grid place-items-center bg-sky-500 text-white border-2 border-sky-300 shrink-0"><Church size={16} /></span>
              <span className="text-[12px] font-semibold text-white truncate max-w-[90px]">{churchLabel}</span>
            </button>
          )}
        </>
      )}
      <div className="flex-1" />
      <button type="button" onClick={onMore} aria-label={t("story.more_title", "Options")} className={`${roundBtn} relative`} data-testid="story-fs-more-btn" data-scheduled={scheduled ? 1 : 0}>
        <MoreHorizontal size={20} />
        {scheduled && !editMode && <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-black" style={{ background: "var(--yalola-primary)" }} />}
      </button>
      {editMode ? (
        <button type="button" onClick={onSave} disabled={busy} className="h-12 pl-4 pr-5 rounded-full text-white text-sm font-bold flex items-center gap-1.5 shadow-lg active:scale-95 transition-transform disabled:opacity-50" style={{ background: "var(--yalola-primary)", boxShadow: "0 8px 24px -6px var(--yalola-primary)" }} data-testid="story-fs-save-btn">
          <Check size={18} /> {busy ? t("story.publishing", "Publication…") : t("story.save", "Enregistrer")}
        </button>
      ) : (
        <button type="button" onClick={onShare} disabled={busy} aria-label={t("story.publish_now", "Partager")} className="h-12 w-12 rounded-full text-white grid place-items-center shadow-lg active:scale-90 transition-transform disabled:opacity-50" style={{ background: "var(--yalola-primary)", boxShadow: "0 8px 24px -6px var(--yalola-primary)" }} data-testid="story-publish-share-btn">
          <ArrowRight size={22} />
        </button>
      )}
    </div>
  );
};

// Corbeille qui apparaît pendant le glisser d'un sticker.
export const TrashZone = forwardRef(({ visible, hot, label }, ref) => (
  <div
    ref={ref}
    className={`absolute left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-1 transition-all duration-200 ${visible ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"}`}
    style={{ bottom: "calc(var(--sa-bottom, 0px) + 28px)" }}
    data-testid="story-fs-trash"
    data-hot={hot ? 1 : 0}
  >
    <div className={`h-14 w-14 rounded-full grid place-items-center border-2 text-white transition-transform ${hot ? "bg-red-600 border-red-300 scale-110" : "bg-black/60 border-white/40"}`}>
      <Trash2 size={24} className={hot ? "animate-pulse" : ""} />
    </div>
    <span className="text-[11px] text-white/85 font-medium">{label}</span>
  </div>
));
TrashZone.displayName = "TrashZone";

// Curseur vertical (0 en bas → 1 en haut) pour la taille du texte / l'épaisseur du trait.
export const VSlider = ({ value, onChange, testId, className = "" }) => {
  const ref = useRef(null);
  const downRef = useRef(false);
  const set = (e) => {
    const r = ref.current.getBoundingClientRect();
    onChange(clamp(1 - (e.clientY - r.top) / r.height, 0, 1));
  };
  return (
    <div
      ref={ref}
      // `relative` seulement si l'appelant ne positionne pas le curseur lui-même (sinon `absolute` était écrasé
      // par `relative` → le curseur du texte se retrouvait au centre, sur la saisie, au lieu du bord gauche).
      className={`${/\babsolute\b/.test(className) ? "" : "relative"} w-11 touch-none ${className}`}
      style={{ height: "36vh" }}
      onPointerDown={(e) => { downRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); set(e); }}
      onPointerMove={(e) => { if (downRef.current) set(e); }}
      onPointerUp={() => { downRef.current = false; }}
      onPointerCancel={() => { downRef.current = false; }}
      role="slider"
      aria-valuenow={Math.round(value * 100)}
      data-testid={testId}
    >
      <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 rounded-full bg-white/35" />
      <div className="absolute left-1/2 -translate-x-1/2 h-6 w-6 rounded-full bg-white shadow-lg" style={{ top: `calc(${(1 - value) * 100}% - 12px)` }} />
    </div>
  );
};
